import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    effect,
    inject,
    linkedSignal,
    output,
    viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Connect25SystemAlertsCoordinatorStore } from "./connect25.system.alerts.coordinator";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { LoginStore } from "../../../stores/login.store";
import { S25ClickOutsideDirective } from "../../s25-click-outside/s25.click.outside.directive";
import { S25CardCarouselComponent } from "../../../standalone/card-carousel/s25.card.carousel.component";
import { Connect25SystemAlertCardComponent } from "./connect25.system.alert.card.component";

@TypeManagerDecorator("s25-ng-connect25-system-alerts")
@Component({
    selector: "s25-ng-connect25-system-alerts",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25ModalModule,
        S25ClickOutsideDirective,
        S25CardCarouselComponent,
        Connect25SystemAlertCardComponent,
    ],
    host: { class: "connect25-system-alerts-host" },
    template: `
        @if (coordinator.hasStudentEngagement()) {
            @if (coordinator.bannerAlerts().length && !coordinator.bannerCollapsed()) {
                <div
                    s25-ng-click-outside
                    class="connect25-sys-alert-banners"
                    id="connect25-alerts-region"
                    (clickOutside)="coordinator.collapseBanner()"
                >
                    <span class="connect25-sys-alert-banners__title">Active alerts</span>
                    <s25-ng-card-carousel ariaLabel="System notices" itemNoun="notice">
                        @for (
                            alert of coordinator.bannerAlerts();
                            track alert.engagementSystemAlertId;
                            let index = $index
                        ) {
                            <connect25-system-alert-card
                                [alert]="alert"
                                [index]="index"
                                [total]="coordinator.bannerAlerts().length"
                                [expandBody]="true"
                                (dismiss)="onBannerDismiss($event, index)"
                            />
                        }
                    </s25-ng-card-carousel>
                </div>
            }

            <s25-ng-modal
                #queuedModal
                [title]="'Notice'"
                [type]="'custom'"
                [size]="'md'"
                (modalClosed)="queuedModalClosed.set(true)"
            >
                @if (coordinator.modalAlerts().length) {
                    <s25-ng-card-carousel ariaLabel="System notices" itemNoun="notice" [fillHeight]="false">
                        @for (
                            alert of coordinator.modalAlerts();
                            track alert.engagementSystemAlertId;
                            let index = $index
                        ) {
                            <connect25-system-alert-card
                                [alert]="alert"
                                [index]="index"
                                [total]="coordinator.modalAlerts().length"
                                [clampLines]="10"
                                (dismiss)="onModalDismiss($event)"
                            />
                        }
                    </s25-ng-card-carousel>
                }
            </s25-ng-modal>
        }

        <!-- Outside SE gate so viewChild(viewAllModal) exists when the shell opens this modal (including zero alerts). -->
        <s25-ng-modal #viewAllModal [title]="'Active alerts'" [type]="'custom'" [size]="'lg'">
            @if (!coordinator.hasStudentEngagement()) {
                <p class="connect25-sys-alert-modal-empty">Active alerts are not available for this instance.</p>
            } @else if (coordinator.alertsLoading()) {
                <p class="connect25-sys-alert-modal-empty">Loading notices…</p>
            } @else if (coordinator.allActiveAlerts().length === 0) {
                <p class="connect25-sys-alert-modal-empty">No active alerts.</p>
            } @else {
                <s25-ng-card-carousel ariaLabel="Active system notices" itemNoun="notice" [fillHeight]="false">
                    @for (
                        alert of coordinator.allActiveAlerts();
                        track alert.engagementSystemAlertId;
                        let index = $index
                    ) {
                        <connect25-system-alert-card
                            [alert]="alert"
                            [index]="index"
                            [total]="coordinator.allActiveAlerts().length"
                            [clampLines]="10"
                            [showAcknowledge]="false"
                        />
                    }
                </s25-ng-card-carousel>
            }
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: block;
        }

        .connect25-sys-alert-banners {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 11;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            background: var(--color--surface-bg);
            border-bottom: 1px solid var(--color--border);
            box-shadow: var(--shadow--dropdown);
            padding-inline: 1.25rem;
            padding-block: 0.5rem 0.75rem;
        }

        .connect25-sys-alert-banners__title {
            font-weight: 600;
            font-size: 0.9375rem;
            color: var(--color--text-strong);
        }

        @media (max-width: 930px) {
            .connect25-sys-alert-banners {
                height: calc(100dvh - 100%);
                min-height: 0;
            }

            .connect25-sys-alert-banners s25-ng-card-carousel {
                flex: 1;
                min-height: 0;
            }
        }

        .connect25-sys-alert-modal-empty {
            margin: 0;
            color: var(--color--text-muted);
        }
    `,
})
export class Connect25SystemAlertsComponent {
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    protected readonly loginStore = inject(LoginStore);
    protected readonly coordinator = inject(Connect25SystemAlertsCoordinatorStore);

    /** Last banner alert acknowledged: the strip is about to unmount, so the banner must move focus somewhere that persists. */
    readonly bannerEmptied = output<void>();

    private readonly queuedModal = viewChild<S25ModalComponent>("queuedModal");
    private readonly viewAllModalRef = viewChild<S25ModalComponent>("viewAllModal");

    /** Last menu tick we opened for — `openAllAlertsTick` is monotonic and never resets, so we must not reopen on every effect run. */
    private lastHandledOpenAllAlertsTick = 0;

    /** Once the user closes the queued modal (X / Escape / last acknowledged), do not auto-reopen until the alerts reload. */
    readonly queuedModalClosed = linkedSignal(() => {
        // Re-seed to false whenever login changes (which reloads the alerts).
        this.loginStore.loggedIn();
        return false;
    });

    constructor() {
        // Open the queued modal once when there are modal alerts (it hosts the whole strip).
        effect(() => {
            const queuedAlerts = this.coordinator.modalAlerts();
            const modalComponent = this.queuedModal();
            if (!queuedAlerts.length || !modalComponent || modalComponent.isOpen() || this.queuedModalClosed()) {
                return;
            }
            void modalComponent.open();
        });

        effect(() => {
            const tickCount = this.coordinator.openAllAlertsTick();
            if (tickCount < 1 || tickCount === this.lastHandledOpenAllAlertsTick) {
                return;
            }
            const viewAllComponent = this.viewAllModalRef();
            if (!viewAllComponent) {
                return;
            }
            this.lastHandledOpenAllAlertsTick = tickCount;
            void viewAllComponent.open();
        });
    }

    onBannerDismiss(alertId: number, dismissedIndex: number): void {
        const currentAlerts = this.coordinator.bannerAlerts();

        if (currentAlerts.length <= 1) {
            this.coordinator.dismiss(alertId);
            this.bannerEmptied.emit();
            return;
        }

        const successor = currentAlerts[dismissedIndex + 1] ?? currentAlerts[dismissedIndex - 1];
        this.coordinator.dismiss(alertId);
        setTimeout(() => this.focusBannerCardAck(successor?.engagementSystemAlertId ?? null), 0);
    }

    onModalDismiss(alertId: number): void {
        const remaining = this.coordinator.modalAlerts().length;
        this.coordinator.dismiss(alertId);
        if (remaining <= 1) {
            void this.queuedModal()?.close();
        }
    }

    private focusBannerCardAck(alertId: number | null): void {
        if (alertId == null) return;
        const host = this.hostRef.nativeElement;
        const ackButton = host.querySelector<HTMLElement>(
            `connect25-system-alert-card[data-alert-id="${alertId}"] .connect25-sys-alert-card__ack`,
        );
        if (!ackButton) return;
        ackButton.focus();
        const reducedMotion = this.reduceMotionQuery.matches;
        const card = ackButton.closest<HTMLElement>("connect25-system-alert-card");
        card?.scrollIntoView({ block: "nearest", inline: "start", behavior: reducedMotion ? "auto" : "smooth" });
    }
}
